/**
 * FLAG:useDocuAi — ONE rule for "the drawer previews this document with the media player".
 *
 * There are two gates on the way to the player, and they used to disagree:
 *
 *   · the drawer's `hasNoPreview` decides whether the preview PANE is mounted at all, and
 *     routes video/audio on the mimetype alone;
 *   · document-drawer-preview's `isPlayableMedia` decides which viewer the mounted pane
 *     renders, and additionally required `minioId`.
 *
 * That gap is a defect, not a detail. The drawer REPLACES its document object wholesale
 * several times right after an upload — every FILE:ANALYSED, image-stats and access event
 * dispatches `fetchDocumentDetails` — and a replacement that does not (yet) carry `minioId`
 * flipped only the inner gate. The pane stayed, but its content swapped player → image
 * branch → player, and each swap unmounts the <video>, revokes its blob and downloads the
 * whole file again. What the user sees is the preview blinking, over and over, while the
 * upload is still being analysed.
 *
 * A PDF never showed this because its branch is keyed on the mimetype alone; `minioId` only
 * picks its loading state. So the rule here is the PDF rule: the MIMETYPE decides which
 * viewer you get, and a missing `minioId` is a loading state inside that viewer — never a
 * reason to become a different viewer.
 */

/** video/* and audio/*, tolerant of casing and of a document that has no mimetype yet. */
export const isMediaMimetype = (mimetype?: string): boolean =>
  /^(video|audio)\//.test((mimetype || '').toLowerCase())

/**
 * Deliberately NOT a function of `minioId` — see the module comment. `useDocuAiEnabled` is
 * the first term so that flag-OFF this is constant `false` by construction, which is what
 * keeps the whole media path out of the baseline drawer.
 */
export const isPlayableMediaPreview = (
  { mimetype }: { mimetype?: string },
  useDocuAiEnabled: boolean,
): boolean => !!useDocuAiEnabled && isMediaMimetype(mimetype)
